/* /dinner-plate/e/:slug — public guest ordering page (no auth) */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Utensils, CheckCircle2, Loader2, Plus, Minus, Sparkles, Clock, Lock } from "lucide-react";
import { dpGuest } from "../lib/dinnerPlateApi";
import { toast } from "sonner";

const CCY_SYM = { GBP: "£", EUR: "€", USD: "$" };
function priceFmt(p, sym) { return p == null ? "" : `${sym}${Number(p).toFixed(2)}`; }

export default function DinnerPlateGuestPage() {
  const { slug } = useParams();
  const [evt, setEvt] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState({});         // { itemId: number }
  const [opt, setOpt] = useState({});         // { itemId: choiceLabel }
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // {order_number, guest_name}

  // Edit-existing-order flow
  const [editCode, setEditCode] = useState("");
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [editingCode, setEditingCode] = useState(null);  // set to DP-XXXXXX once loaded → submit becomes PATCH

  useEffect(() => {
    dpGuest.event(slug).then(setEvt).catch(() => setNotFound(true));
  }, [slug]);

  function bump(itemId, delta) {
    setQty(q => {
      const cur = q[itemId] || 0;
      const next = Math.max(0, Math.min(10, cur + delta));
      return { ...q, [itemId]: next };
    });
  }

  async function submit() {
    if (!name.trim()) { toast.error("Enter your name"); return; }
    const items = Object.entries(qty).filter(([, n]) => n > 0)
      .map(([id, n]) => ({ menu_item_id: id, selected_option: opt[id] || null, quantity: n }));
    if (items.length === 0) { toast.error("Pick at least one item"); return; }
    setBusy(true);
    try {
      const payload = {
        guest_name: name.trim(), guest_email: email.trim() || null,
        special_dietary_notes: notes.trim(), items,
      };
      const r = editingCode
        ? await dpGuest.editOrder(slug, editingCode, payload)
        : await dpGuest.submitOrder(slug, payload);
      setConfirmation(r);
      toast.success(editingCode ? "Order updated" : "Order received");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit");
    } finally { setBusy(false); }
  }

  async function loadExisting() {
    if (!editCode.trim()) return;
    setLoadingEdit(true);
    try {
      const o = await dpGuest.getOrder(slug, editCode.trim().toUpperCase());
      setName(o.guest_name); setEmail(o.guest_email || ""); setNotes(o.special_dietary_notes || "");
      const nq = {}, np = {};
      for (const it of o.items) { nq[it.menu_item_id] = it.quantity; if (it.selected_option) np[it.menu_item_id] = it.selected_option; }
      setQty(nq); setOpt(np); setConfirmation(null);
      setEditingCode(o.order_number);
      toast.success("Loaded your previous order — make changes below");
    } catch { toast.error("No order found with that code"); }
    finally { setLoadingEdit(false); }
  }

  function cancelEdit() {
    setEditingCode(null);
    setName(""); setEmail(""); setNotes(""); setQty({}); setOpt({});
    setEditCode("");
  }

  if (notFound) {
    return <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg)" }}>
      <div className="text-center"><h1 className="text-2xl font-display font-bold mb-2">Event not found</h1><p className="text-sm" style={{ color: "var(--text-muted)" }}>The link may have been revoked or mistyped.</p></div>
    </div>;
  }
  if (!evt) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const sym = evt.currency_symbol || CCY_SYM[evt.currency] || "£";
  const deadlineDate = evt.order_deadline ? new Date(evt.order_deadline) : null;

  // Friendly closed screen when the deadline has passed and we're not already editing.
  if (evt.orders_closed && !editingCode && !confirmation) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg)" }}>
        <div className="card-tactile rounded-2xl p-8 max-w-md text-center" data-testid="dp-orders-closed">
          <Lock className="h-12 w-12 mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
          <h1 className="text-2xl font-display font-black tracking-tight mb-2">Orders are closed</h1>
          <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
            The host closed the pre-order for <span className="font-semibold" style={{ color: "var(--text-main)" }}>{evt.title}</span> on {deadlineDate.toLocaleString()}.
          </p>
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>If this was a mistake, please contact the host directly.</p>
        </div>
      </div>
    );
  }

  // Live guest total (uses menu prices)
  const menuById = {};
  for (const c of evt.categories) for (const it of c.items) menuById[it.id] = it;
  let liveTotal = 0;
  let hasAnyPrice = false;
  for (const [iid, n] of Object.entries(qty)) {
    const it = menuById[iid];
    if (n > 0 && it?.price != null) { liveTotal += Number(it.price) * n; hasAnyPrice = true; }
  }
  liveTotal = Number(liveTotal.toFixed(2));

  if (confirmation) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg)" }}>
        <div className="card-tactile rounded-2xl p-8 max-w-md text-center" data-testid="dp-confirmation">
          <CheckCircle2 className="h-12 w-12 mx-auto mb-3" style={{ color: "var(--primary)" }} />
          <h1 className="text-2xl font-display font-black tracking-tight mb-2">
            {confirmation.updated_at && confirmation.updated_at !== confirmation.created_at ? "Order updated!" : "Order received!"}
          </h1>
          <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
            Save your order number to edit later.
          </p>
          <div className="rounded-xl p-4 mb-4" style={{ background: "var(--primary-soft)", color: "var(--primary-ink)" }}>
            <div className="label-caps">Your order number</div>
            <div data-testid="dp-order-code" className="text-3xl font-display font-black tracking-tight mt-1">{confirmation.order_number}</div>
          </div>
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>Thanks, {confirmation.guest_name}!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text-main)" }} data-testid="dp-guest-page">
      <div className="max-w-2xl mx-auto p-4 sm:p-6 pb-40">
        <header className="text-center mb-6">
          <Utensils className="h-8 w-8 mx-auto mb-2" style={{ color: "var(--primary-ink)" }} />
          <h1 className="text-3xl sm:text-4xl font-display font-black tracking-tighter">{evt.title}</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            {evt.restaurant_name}{evt.event_date && ` · ${new Date(evt.event_date).toLocaleString()}`}
          </p>
        </header>

        {/* Deadline banner — only when a deadline is set and not yet passed */}
        {deadlineDate && !evt.orders_closed && (
          <div className="rounded-xl p-3 mb-4 flex items-center gap-2 text-sm" data-testid="dp-deadline-banner"
               style={{ background: "var(--primary-soft)", color: "var(--primary-ink)" }}>
            <Clock className="h-4 w-4 shrink-0" />
            <span><strong>Orders close</strong> {deadlineDate.toLocaleString()}</span>
          </div>
        )}

        {/* Edit existing order banner */}
        {editingCode ? (
          <div className="card-tactile rounded-xl p-3 mb-5 flex items-center justify-between gap-3" data-testid="dp-editing-banner"
               style={{ background: "var(--primary-soft)", color: "var(--primary-ink)" }}>
            <div className="text-sm">
              <span className="font-semibold">Editing order</span>{" "}
              <span className="font-mono text-xs">{editingCode}</span>
            </div>
            <button onClick={cancelEdit} data-testid="dp-cancel-edit"
                    className="text-xs font-semibold underline">Cancel edit</button>
          </div>
        ) : (
          <details className="card-tactile rounded-xl p-3 mb-5">
            <summary className="text-sm font-semibold cursor-pointer flex items-center gap-1"><Sparkles className="h-4 w-4" /> Already ordered? Edit with your code</summary>
            <div className="flex gap-2 mt-3">
              <input value={editCode} onChange={e => setEditCode(e.target.value.toUpperCase())} placeholder="DP-XXXXXX" data-testid="dp-edit-code"
                     className="flex-1 px-3 h-9 rounded-lg border bg-transparent text-sm font-mono"
                     style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }} />
              <button onClick={loadExisting} disabled={loadingEdit} data-testid="dp-edit-load"
                      className="h-9 px-3 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                      style={{ background: "var(--primary)" }}>
                {loadingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : "Load"}
              </button>
            </div>
          </details>
        )}

        {/* Menu */}
        {evt.categories.map((cat, ci) => (
          <section key={ci} className="mb-6" data-testid={`dp-cat-${ci}`}>
            <h2 className="font-display font-bold tracking-tight text-xl mb-3" style={{ color: "var(--primary-ink)" }}>{cat.name}</h2>
            <div className="space-y-2">
              {cat.items.map(it => {
                const n = qty[it.id] || 0;
                return (
                  <div key={it.id} data-testid={`dp-item-${it.id}`} className="card-tactile rounded-xl p-4 flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm">{it.title} {it.price != null && <span className="text-xs font-normal" style={{ color: "var(--text-muted)" }}>· {priceFmt(it.price, sym)}</span>}</div>
                      {it.description && <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{it.description}</div>}
                      {it.dietary_tags?.length > 0 && (
                        <div className="mt-1 flex gap-1">{it.dietary_tags.map(t => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "var(--surface-2)", color: "var(--text-muted)" }}>{t}</span>)}</div>
                      )}
                      {n > 0 && it.options?.length > 0 && it.options[0].choices.length > 0 && (
                        <select value={opt[it.id] || ""} onChange={e => setOpt(o => ({ ...o, [it.id]: e.target.value }))}
                                data-testid={`dp-opt-${it.id}`}
                                className="mt-2 text-xs px-2 h-8 rounded border bg-transparent"
                                style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}>
                          <option value="">{it.options[0].name}…</option>
                          {it.options[0].choices.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => bump(it.id, -1)} disabled={n === 0}
                              data-testid={`dp-minus-${it.id}`}
                              className="h-8 w-8 rounded-lg border flex items-center justify-center disabled:opacity-40"
                              style={{ borderColor: "var(--border-subtle)" }}><Minus className="h-3 w-3" /></button>
                      <span className="w-6 text-center text-sm font-semibold" data-testid={`dp-qty-${it.id}`}>{n}</span>
                      <button onClick={() => bump(it.id, +1)}
                              data-testid={`dp-plus-${it.id}`}
                              className="h-8 w-8 rounded-lg text-white flex items-center justify-center"
                              style={{ background: "var(--primary)" }}><Plus className="h-3 w-3" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        {/* Guest info + submit */}
        <section className="card-tactile rounded-2xl p-5 space-y-3">
          <label className="block">
            <span className="label-caps block mb-1">Your name *</span>
            <input value={name} onChange={e => setName(e.target.value)} data-testid="dp-guest-name" required
                   className="w-full px-3 h-10 rounded-lg border bg-transparent text-sm"
                   style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }} />
          </label>
          <label className="block">
            <span className="label-caps block mb-1">Email (optional)</span>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="dp-guest-email"
                   className="w-full px-3 h-10 rounded-lg border bg-transparent text-sm"
                   style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }} />
          </label>
          <label className="block">
            <span className="label-caps block mb-1">Allergies / notes</span>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} data-testid="dp-guest-notes"
                      className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm"
                      style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }} />
          </label>
          <button onClick={submit} disabled={busy} data-testid="dp-submit-order"
                  className="w-full h-11 rounded-lg text-sm font-semibold text-white disabled:opacity-60 inline-flex items-center justify-center gap-2"
                  style={{ background: "var(--primary)" }}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {editingCode ? "Update order" : "Submit order"}
            {hasAnyPrice && <span data-testid="dp-live-total" className="opacity-90">· {sym}{liveTotal.toFixed(2)}</span>}
          </button>
        </section>
      </div>
    </div>
  );
}
